import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/api";

const MOVEMENT_LABELS = {
  "all": "Allmänt",
  "klarhet": "Klarhet",
  "frigorelse": "Frigörelse",
  "forankring": "Förankring"
};

const QuotesPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all-quotes");

  useEffect(() => {
    API.get("/quotes").then(res => {
      setQuotes(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleFavorite = async (quoteId) => {
    try {
      const res = await API.post(`/quotes/${quoteId}/favorite`);
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, is_favorite: res.data.is_favorite } : q));
    } catch {}
  };

  const filtered = filter === "favorites"
    ? quotes.filter(q => q.is_favorite)
    : filter === "all-quotes"
    ? quotes
    : quotes.filter(q => q.movement === filter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4">Visdomsord</p>
            <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111] mb-3" data-testid="quotes-page-title">Citatsamling</h1>
            <p className="text-[#111111]/50 text-base max-w-md mx-auto leading-relaxed">
              Ord att bära med sig. Spara dina favoriter.
            </p>
          </motion.div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {[
              { id: "all-quotes", label: "Alla" },
              { id: "favorites", label: "Favoriter" },
              { id: "klarhet", label: "Klarhet" },
              { id: "frigorelse", label: "Frigörelse" },
              { id: "forankring", label: "Förankring" },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${
                  filter === f.id ? 'bg-[#A0655A] text-white' : 'bg-[#F9F3EF] text-[#111111]/50 hover:bg-[#EBE3DF]'
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Quotes grid */}
          <div className="space-y-4">
            {filtered.length > 0 ? filtered.map((quote, idx) => (
              <motion.div
                key={quote.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * idx }}
                className="bg-white border border-[#EBE3DF] rounded-2xl p-6 group"
                data-testid={`quote-${quote.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="heading-font text-lg text-[#111111] leading-relaxed italic mb-2">
                      "{quote.text}"
                    </p>
                    <p className="text-xs text-[#111111]/30">{MOVEMENT_LABELS[quote.movement] || quote.movement}</p>
                  </div>
                  <button
                    onClick={() => toggleFavorite(quote.id)}
                    className="flex-shrink-0 p-2 rounded-full hover:bg-[#FBF5F2] transition-colors"
                    data-testid={`fav-${quote.id}`}
                  >
                    <Heart
                      size={18}
                      className={quote.is_favorite ? 'fill-[#A0655A] text-[#A0655A]' : 'text-[#111111]/20'}
                    />
                  </button>
                </div>
              </motion.div>
            )) : (
              <div className="text-center py-12">
                <p className="text-[#111111]/30 text-sm">
                  {filter === "favorites" ? "Du har inga favoriter ännu." : "Inga citat hittades."}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default QuotesPage;
